import is from '@sindresorhus/is';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import { skillTypeLiteralSchema } from '~/shared/data-contract/filter-response';
import type { FilterWithFilterStates } from '~/shared/data-contract/filter-state';
import {
    createdAtFilterStateSchema,
    createdAtLiteralSchema,
    createPreDefinedFilterSchemas,
    createSortFilterStateByOrder,
    customFilterKeySchema,
    customFilterStateSchema,
    getFilterValuesFromFilterValueString,
    providerFilterStateSchema,
    providerLiteralSchema,
    reduceFiltersToKeyValuePairs,
    setFilterStateValuesToFilter,
    skillFilterStateSchema,
    updatedAtFilterStateSchema,
    updatedAtLiteralSchema,
} from '~/shared/data-contract/filter-state';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import type { FilterInFilterResponseTransformed } from '../../data-contract/filter-response-transformed';
import { filterResponseTransformedSchema } from '../../data-contract/filter-response-transformed';
import { convertFilterStateToFilters } from '../common/filter-sidebar/utils';

const allowedLearningOpportunityFilterInOrder = [
    skillFilterStateSchema,
    providerFilterStateSchema,
    updatedAtFilterStateSchema,
    createdAtFilterStateSchema,
] as const;

const filterOrder = allowedLearningOpportunityFilterInOrder.map(({ shape }) => shape.field.value);
const allowedLearningOpportunityFilterFieldNameSchema = z.union([
    skillTypeLiteralSchema,
    providerLiteralSchema,
    updatedAtLiteralSchema,
    createdAtLiteralSchema,
]);
const allowedLearningOpportunityAndCustomFilterFieldNameSchema =
    allowedLearningOpportunityFilterFieldNameSchema.or(customFilterKeySchema);
const allowedLearningOpportunityFilterSchema = z.union(allowedLearningOpportunityFilterInOrder);
const allowedLearningOpportunityAndCustomFilterSchema =
    allowedLearningOpportunityFilterSchema.or(customFilterStateSchema);

type AllowedLearningOpportunityFilter = z.infer<typeof allowedLearningOpportunityFilterSchema>;

type AllowedLearningOpportunityOrCustomFilter = z.infer<typeof allowedLearningOpportunityAndCustomFilterSchema>;

const sortFilters = createSortFilterStateByOrder({ filterOrder });

const isAllowedOrCustomFilter = (
    filter: FilterInFilterResponseTransformed,
): filter is AllowedLearningOpportunityOrCustomFilter =>
    allowedLearningOpportunityAndCustomFilterFieldNameSchema.safeParse(filter.field).success;

const isAllowedFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedLearningOpportunityFilter =>
    allowedLearningOpportunityFilterFieldNameSchema.safeParse(filter.field).success;

const filterQueryParam = 'talentDirectoryLearningOpportunityFilters';

export type UseTalentDirectoryLearningOpportunityFilterResult = {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

export const useTalentDirectoryLearningOpportunityFilter = ({
    enabled,
}: {
    readonly enabled: boolean;
}): UseQueryResult<UseTalentDirectoryLearningOpportunityFilterResult> => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language, locationSearchParams } = applicationState;
    const pathname: Pathname = `/global/directory/learning-opportunities/filter`;

    const resetFilterValues = (): void => {
        applicationState.locationSearchParams.delete(filterQueryParam);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    const saveFilterValues = (updatedFilters: readonly FilterWithFilterStates[]): void => {
        const filterKeyValuePairs = reduceFiltersToKeyValuePairs(updatedFilters);

        if (is.emptyObject(filterKeyValuePairs)) {
            resetFilterValues();
        } else {
            applicationState.locationSearchParams.set(filterQueryParam, JSON.stringify(filterKeyValuePairs));
            actions.setLocationSearchParams(applicationState.locationSearchParams);
        }
    };

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(filterResponseTransformedSchema, {
                pathname,
            });

            if (result.isOk) {
                const allowedLearningOpportunityFilterSchemas = createPreDefinedFilterSchemas({ translate });
                const filterStates = result.value
                    .filter(isAllowedOrCustomFilter)
                    .sort(sortFilters)
                    .map((filter) => {
                        if (isAllowedFilter(filter)) {
                            const filterResult =
                                allowedLearningOpportunityFilterSchemas[filter.field].safeParse(filter);

                            if (filterResult.success) {
                                return filterResult.data;
                            }
                        }

                        return {
                            ...filter,
                            label: translate(`attributes.learningOpportunity.${filter.field.replace('custom.', '')}`),
                        };
                    });

                return [
                    {
                        id: 'skills',
                        filterStates: filterStates.filter(({ field }) => field === skillTypeLiteralSchema.value),
                        subTitle: translate('pages.learning-opportunities.filters.skills-preferences.sub-title'),
                        title: translate('pages.learning-opportunities.filters.skills-preferences.title'),
                    },
                    {
                        id: 'workSpace',
                        filterStates: filterStates.filter(({ field }) => field !== skillTypeLiteralSchema.value),
                        subTitle: translate('pages.learning-opportunities.filters.workspace-preferences.sub-title'),
                        title: translate('pages.learning-opportunities.filters.workspace-preferences.title'),
                    },
                ];
            }
            throw result.error;
        },
        queryKey: [pathname, 'get', language.code],
        select(filters) {
            const filterValuesJsonString = locationSearchParams.get(filterQueryParam);

            if (is.string(filterValuesJsonString)) {
                const filterValuesFromStorage = getFilterValuesFromFilterValueString({
                    filters,
                    filterValuesJsonString,
                });

                if (filterValuesFromStorage.isOk) {
                    const filtersWithValues = setFilterStateValuesToFilter(filters, filterValuesFromStorage.value);
                    const allFilterStatesWithValues = filtersWithValues.reduce(
                        // biome-ignore lint/performance/noAccumulatingSpread: suppressed due to migration
                        (acc, { filterStates }) => [...acc, ...filterStates],
                        [],
                    );

                    return {
                        filterPayload: Maybe.of(convertFilterStateToFilters(allFilterStatesWithValues)),
                        filters: filtersWithValues,
                        hasActiveFilters: allFilterStatesWithValues.some(({ value }) => !is.nullOrUndefined(value)),
                        resetFilterValues,
                        saveFilterValues,
                    };
                }
            }

            return {
                filterPayload: Maybe.nothing<GeneralFilter>(),
                filters,
                hasActiveFilters: false,
                resetFilterValues,
                saveFilterValues,
            };
        },
    });
};
